import { Checkbox } from '@mui/material'
import React from 'react'
import {
  CMBCEnhancedPhoneInput,
  RectangleRadioButton,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import {
  PIFormSection,
  PISectionTitle,
  PISectionValidation,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsPageStyles'
import { CustomHTMLSelect } from 'src/consumer/components/helperComponents'
import { RELATIONSHIP_OPTIONS } from 'src/consumer/constants'
import PatientSearchDropdown from './PatientSearchDropdown'
import { FormField, DisplayFormField, CheckboxFormField } from './FormField'
import {
  GRID_CONFIG,
  GENDER_OPTIONS,
  FORM_CONSTANTS,
  getImportIdLabel,
} from '../utils/patientFormConfig'

export const PatientBasicInfoFields = ({
  patientDetails,
  editMode = true,
  formHook,
  validationHook,
  searchHook,
  visibilityHook,
  makeAnonymousPatient,
  isForcingAnonymousPatient = false,
  importIdFieldLabel = 'Import Id',
  clinicId,
  clinicBrandId,
  patientSearchApiSignature,
  disablePatientSearch = false,
}) => {
  const handleAgeKeyDown = (e) => {
    if (e.key === '.' || e.key === ',') {
      e.preventDefault()
    }
  }

  return (
    <PIFormSection container spacing={2}>
      {/* Forcing Anonymous Patient Warning */}
      {isForcingAnonymousPatient && (
        <div className="text-info">
          You are not allowed to create a patient with contact details in this clinic.
        </div>
      )}

      {/* Relation Dropdown */}
      {visibilityHook.shouldShowRelationDropdown && (
        <PIFormSection size={GRID_CONFIG}>
          <div style={{ marginBottom: '1rem' }}>
            Relation to <b>{patientDetails?.relatedToExistingPatient?.name}</b>:
            <CustomHTMLSelect
              value={patientDetails?.dependentRelation}
              onChange={formHook.handleRelationChange}
              getOptionLabel={(data) => data?.label}
              getOptionValue={(data) => data?.value}
              options={RELATIONSHIP_OPTIONS}
              disabled={!editMode}
            />
          </div>
        </PIFormSection>
      )}

      {/* Mobile Field */}
      {visibilityHook.isFieldVisible('mobile') && !makeAnonymousPatient && (
        <PIFormSection size={GRID_CONFIG}>
          <PISectionTitle>Mobile*</PISectionTitle>
          <div className="d-flex flex-column">
            <div style={{ position: 'relative' }}>
              <CMBCEnhancedPhoneInput
                inputProps={{ ref: searchHook.inputRef }}
                disabled={!editMode}
                onFocus={searchHook.handleMobileFocus}
                onBlur={searchHook.handleMobileBlur}
                onKeyDown={searchHook.handlePhoneInputKeyDown}
                placeholder="Mobile Number"
                value={patientDetails?.mobile}
                onChange={(value) => {
                  searchHook.clearLoadedOptions()
                  formHook.handleMobileChange(value)
                }}
              />

              {!disablePatientSearch && (
                <PatientSearchDropdown
                  searchHook={searchHook}
                  clinicId={clinicId}
                  clinicBrandId={clinicBrandId}
                  patientSearchApiSignature={patientSearchApiSignature}
                />
              )}
            </div>
            {validationHook.getFieldValidation('mobile').showError && (
              <PISectionValidation>Required</PISectionValidation>
            )}
          </div>
        </PIFormSection>
      )}

      {/* Anonymous Patient Checkbox */}
      {visibilityHook.shouldShowAnonymousCheckbox && (
        <CheckboxFormField
          label="Patient does not have a phone/email"
          checked={!!makeAnonymousPatient}
          onChange={(e) => formHook.handleAnonymousPatientChange(e.target.checked)}
          checkboxProps={{ size: 'small' }}
          gridConfig={GRID_CONFIG}
        />
      )}

      {/* Name */}
      {visibilityHook.isFieldVisible('name') && (
        <FormField
          label="Name*"
          value={patientDetails?.name}
          onChange={formHook.handleNameChange}
          disabled={!editMode}
          validationError={validationHook.getFieldValidation('name').showError ? 'Required' : null}
        />
      )}

      {/* Gender */}
      {visibilityHook.isFieldVisible('gender') && (
        <PIFormSection size={GRID_CONFIG}>
          <PISectionTitle>Gender*</PISectionTitle>
          <div className="d-flex flex-column">
            <RectangleRadioButton
              disabled={!editMode}
              options={GENDER_OPTIONS}
              selectedId={patientDetails?.gender}
              selectionFunction={formHook.handleGenderChange}
            />
            {validationHook.getFieldValidation('gender').showError && (
              <PISectionValidation>Choose one</PISectionValidation>
            )}
          </div>
        </PIFormSection>
      )}

      {/* Age */}
      {visibilityHook.isFieldVisible('age') && (
        <FormField
          label="Age*"
          type="number"
          value={patientDetails?.age}
          onChange={formHook.handleAgeChange}
          onKeyDown={handleAgeKeyDown}
          disabled={!editMode}
          min={FORM_CONSTANTS.MIN_AGE}
          max={FORM_CONSTANTS.MAX_AGE}
          validationError={validationHook.getFieldValidation('age').showError ? 'Required' : null}
        />
      )}

      {/* Email - Basic Info */}
      {visibilityHook.shouldShowEmailInBasicInfo && (
        <FormField
          label="Email"
          value={patientDetails?.email}
          onChange={formHook.handleEmailChange}
          disabled={!editMode}
          validationError={validationHook.getFieldValidation('email').showError ? 'Required' : null}
        />
      )}

      {/* Address */}
      {visibilityHook.isFieldVisible('address') && (
        <FormField
          label="Address"
          value={patientDetails?.address}
          onChange={formHook.handleAddressChange}
          disabled={!editMode}
        />
      )}

      {/* UHID */}
      {visibilityHook.isFieldVisible('uhid') && (
        <FormField
          label="UHID"
          value={patientDetails?.uhId || ''}
          onChange={formHook.handleUhidChange}
          disabled={!editMode}
        />
      )}

      {/* Import ID */}
      {visibilityHook.isFieldVisible('importId') && (
        <FormField
          label={getImportIdLabel(importIdFieldLabel)}
          value={patientDetails?.importData?.importedFromId || ''}
          onChange={formHook.handleImportIdChange}
          disabled={!editMode}
        />
      )}

      {/* Patient Number */}
      {visibilityHook.isFieldVisible('patientNumber') && (
        <DisplayFormField
          label="Patient Number"
          value={patientDetails?.patientNumber}
        />
      )}
    </PIFormSection>
  )
}

export default PatientBasicInfoFields